import { useEffect, useRef } from 'react'
import { useMap } from '@vis.gl/react-google-maps'

export type LatLng = { lat: number; lng: number }

/** Khung bao chữ nhật bọc các điểm — dùng cho fitBounds. */
export function boundsLiteral(points: LatLng[]) {
    const lats = points.map((p) => p.lat)
    const lngs = points.map((p) => p.lng)
    return {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
    }
}

/**
 * Fit bản đồ vào tất cả xe MỘT LẦN (lần đầu có dữ liệu). Tôn trọng thao tác
 * kéo/zoom của người dùng: nếu user đã kéo trước khi có dữ liệu thì không tự fit.
 */
export function FitToVehicles({
    points,
    enabled = true,
}: {
    points: LatLng[]
    enabled?: boolean
}) {
    const map = useMap()
    const doneRef = useRef(false)
    const userRef = useRef(false)

    useEffect(() => {
        if (!map) return
        const l = map.addListener('dragstart', () => {
            userRef.current = true
        })
        return () => l.remove()
    }, [map])

    useEffect(() => {
        if (!map || !enabled || doneRef.current || userRef.current) return
        if (points.length === 0) return
        doneRef.current = true
        if (points.length === 1) {
            map.setCenter(points[0])
            map.setZoom(15)
            return
        }
        map.fitBounds(boundsLiteral(points), 40)
    }, [map, points, enabled])

    return null
}

/**
 * Pan MỘT LẦN khi `trigger` đổi (vd đổi xe được chọn / chọn điểm dừng) — KHÔNG
 * pan lại mỗi khi toạ độ cập nhật, để không giật khi xe di chuyển.
 */
export function PanToSelected({
    position,
    trigger,
    zoom = 15,
}: {
    position: LatLng | null
    trigger?: string | number | null
    zoom?: number
}) {
    const map = useMap()
    const posRef = useRef(position)
    posRef.current = position
    useEffect(() => {
        const pos = posRef.current
        if (!map || !pos || trigger == null) return
        map.panTo(pos)
        map.setZoom(zoom)
    }, [map, trigger, zoom])
    return null
}

/**
 * Chế độ "bám xe" (live): pan theo xe mỗi lần cập nhật vị trí, NHƯNG dừng bám
 * ngay khi người dùng kéo bản đồ (giữ nguyên zoom của user). Vào lại live
 * (component remount) thì bám tiếp.
 */
export function FollowVehicle({ position }: { position: LatLng }) {
    const map = useMap()
    const userRef = useRef(false)
    const { lat, lng } = position
    useEffect(() => {
        if (!map) return
        const l = map.addListener('dragstart', () => {
            userRef.current = true
        })
        return () => l.remove()
    }, [map])
    useEffect(() => {
        if (!map || userRef.current) return
        map.panTo({ lat, lng })
    }, [map, lat, lng])
    return null
}

/** Fit vào tập điểm (lộ trình playback), refit khi tập điểm đổi. */
export function FitToBounds({
    points,
    zoomSingle = 16,
}: {
    points: LatLng[]
    zoomSingle?: number
}) {
    const map = useMap()
    const sig = JSON.stringify(points)
    useEffect(() => {
        if (!map) return
        const pts = JSON.parse(sig) as LatLng[]
        if (pts.length === 0) return
        if (pts.length === 1) {
            map.setCenter(pts[0])
            map.setZoom(zoomSingle)
            return
        }
        map.fitBounds(boundsLiteral(pts), 24)
    }, [map, sig, zoomSingle])
    return null
}
